// Community Options Data - flattened from nested JSON structure
// Each option can be toggled on/off at the community level

export type CommunityOption = {
  id: string
  section: 'exterior' | 'interior' | 'model-elevation' | 'model-structural'
  modelName?: string
  category: string
  lvs: string
  variantSet: string
  page: string // e.g. "Standard", "Level 1", etc.
  variant: string
  enabled: boolean
  subVariants?: { name: string; enabled: boolean }[]
}

// Helper to generate unique IDs
let idCounter = 0
function genId(prefix: string): string {
  return `${prefix}-${++idCounter}`
}

function flattenPageOptions(
  section: CommunityOption['section'],
  category: string,
  lvs: string,
  variantSet: string,
  page: string,
  pageOptions: any[],
  modelName?: string,
): CommunityOption[] {
  return pageOptions.map((opt: any) => {
    const subVariants =
      opt.sub_variant_options && Array.isArray(opt.sub_variant_options)
        ? opt.sub_variant_options.map((sv: any) => ({
            name: sv.sub_variant,
            enabled: sv.enabled,
          }))
        : Object.keys(opt.sub_variant_options || {}).length > 0
          ? undefined
          : undefined
    return {
      id: genId(section),
      section,
      modelName,
      category,
      lvs,
      variantSet,
      page: page || 'Default',
      variant: opt.variant.trim(),
      enabled: opt.enabled,
      subVariants,
    }
  })
}

function flattenCategoryOptions(
  section: CommunityOption['section'],
  categories: any[],
  modelName?: string,
): CommunityOption[] {
  const results: CommunityOption[] = []
  for (const cat of categories) {
    const category = cat.category
    for (const lvsGroup of cat.category_options || []) {
      const lvs = lvsGroup.lvs
      for (const vsGroup of lvsGroup.lvs_options || []) {
        const variantSet = vsGroup.variant_set
        for (const pageGroup of vsGroup.variant_set_options || []) {
          const page = pageGroup.page || ''
          const pageOptions = pageGroup.page_options || []
          results.push(
            ...flattenPageOptions(
              section,
              category,
              lvs,
              variantSet,
              page,
              pageOptions,
              modelName,
            ),
          )
        }
      }
    }
  }
  return results
}

// Raw JSON data
const rawData = {
  Models: [
    {
      Name: 'Bellevue',
      Beds: '3',
      Baths: '2.5',
      Price: '600',
      Surface: '1795',
      Location: 'Calgary',
      Enabled: true,
      Additional_Info: 'Two storey laned home',
      Elevations: [
        {
          category: 'MAIN OPTIONS',
          category_options: [
            {
              lvs: 'Elevations',
              lvs_options: [
                {
                  variant_set: 'Options',
                  variant_set_options: [
                    {
                      page: '',
                      page_options: [
                        {
                          enabled: true,
                          variant: 'Elevation AC',
                          sub_variant_options: {},
                        },
                        {
                          enabled: true,
                          variant: 'Elevation P',
                          sub_variant_options: {},
                        },
                        {
                          enabled: true,
                          variant: 'Elevation S',
                          sub_variant_options: {},
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
      Structural: [
        {
          category: 'LAYOUT OPTIONS',
          category_options: [
            {
              lvs: 'Plan Options',
              lvs_options: [
                {
                  variant_set: 'Main Floor',
                  variant_set_options: [
                    {
                      page: '',
                      page_options: [
                        {
                          enabled: true,
                          variant: 'Standard',
                          sub_variant_options: {},
                        },
                        {
                          enabled: true,
                          variant: 'Primary',
                          sub_variant_options: {},
                        },
                        {
                          enabled: true,
                          variant: 'Full Bath',
                          sub_variant_options: {},
                        },
                      ],
                    },
                  ],
                },
                {
                  variant_set: 'Basement',
                  variant_set_options: [
                    {
                      page: '',
                      page_options: [
                        {
                          enabled: true,
                          variant: 'Undeveloped',
                          sub_variant_options: {},
                        },
                        {
                          enabled: true,
                          variant: 'One Bedroom',
                          sub_variant_options: {},
                        },
                        {
                          enabled: true,
                          variant: 'Two Bedroom',
                          sub_variant_options: {},
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      Name: 'Collingwood',
      Beds: '4',
      Baths: '2.5',
      Price: '800',
      Surface: '2235',
      Location: 'Calgary',
      Enabled: true,
      Additional_Info: 'Front Drive',
      Elevations: [
        {
          category: 'MAIN OPTIONS',
          category_options: [
            {
              lvs: 'Elevations',
              lvs_options: [
                {
                  variant_set: 'Options',
                  variant_set_options: [
                    {
                      page: '',
                      page_options: [
                        {
                          enabled: true,
                          variant: 'Elevation A',
                          sub_variant_options: {},
                        },
                        {
                          enabled: true,
                          variant: 'Elevation CE',
                          sub_variant_options: {},
                        },
                        {
                          enabled: true,
                          variant: 'Elevation U',
                          sub_variant_options: {},
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
      Structural: [
        {
          category: 'LAYOUT OPTIONS',
          category_options: [
            {
              lvs: 'Plan Options',
              lvs_options: [
                {
                  variant_set: 'Main Floor',
                  variant_set_options: [
                    {
                      page: '',
                      page_options: [
                        {
                          enabled: true,
                          variant: 'Standard + Pantry',
                          sub_variant_options: {},
                        },
                        {
                          enabled: true,
                          variant: 'Standard + Spice Kitchen',
                          sub_variant_options: {},
                        },
                        {
                          enabled: true,
                          variant: 'Bedroom + Pantry',
                          sub_variant_options: {},
                        },
                        {
                          enabled: true,
                          variant: 'Bedroom + Spice Kitchen',
                          sub_variant_options: {},
                        },
                        {
                          enabled: true,
                          variant: 'Full Bath + Pantry',
                          sub_variant_options: {},
                        },
                        {
                          enabled: true,
                          variant: 'Full Bath + Spice Kitchen',
                          sub_variant_options: {},
                        },
                      ],
                    },
                  ],
                },
                {
                  variant_set: 'Upper Floor',
                  variant_set_options: [
                    {
                      page: '',
                      page_options: [
                        {
                          enabled: true,
                          variant: 'Standard',
                          sub_variant_options: {},
                        },
                        {
                          enabled: true,
                          variant: '3 Bedroom Optional',
                          sub_variant_options: {},
                        },
                      ],
                    },
                  ],
                },
                {
                  variant_set: 'Basement',
                  variant_set_options: [
                    {
                      page: '',
                      page_options: [
                        {
                          enabled: true,
                          variant: 'Standard',
                          sub_variant_options: {},
                        },
                        {
                          enabled: true,
                          variant: '2 Bedroom Suite 1',
                          sub_variant_options: {},
                        },
                        {
                          enabled: true,
                          variant: '2 Bedroom Suite 2',
                          sub_variant_options: {},
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
  Exterior: [
    {
      category: 'MAIN OPTIONS',
      category_options: [
        {
          lvs: 'Inspirations',
          lvs_options: [
            {
              variant_set: 'Palettes',
              variant_set_options: [
                {
                  page: '1',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Palette B1',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Palette B2',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Palette B3',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Palette B4',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Palette B6',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Palette B8',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Palette B9',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Palette B10',
                      sub_variant_options: {},
                    },
                  ],
                },
                {
                  page: '2',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Palette V1',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Palette V2',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Palette V3',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Palette V4',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Palette S1',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Palette S3',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Palette S4',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Palette S5',
                      sub_variant_options: {},
                    },
                  ],
                },
                {
                  page: '3',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Palette S6',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
  Interior: [
    {
      category: 'MAIN OPTIONS',
      category_options: [
        {
          lvs: 'Bathroom',
          lvs_options: [
            {
              variant_set: 'Fixtures',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Standard',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Upgrade',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Fixture Finish',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Chrome',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Matte Black',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Stainless Steel',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Champagne Bronze',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Cabinet Profile',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    { enabled: true, variant: 'Slab', sub_variant_options: {} },
                    {
                      enabled: true,
                      variant: 'Shaker',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Micro Shaker',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Cabinet Finish',
              variant_set_options: [
                {
                  page: 'Standard',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Fashionista',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Free Spirit',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'First Class',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'The Chameleon',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Natural Affinity',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Black',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Suede Stone',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Fresh Linen',
                      sub_variant_options: {},
                    },
                  ],
                },
                {
                  page: 'Level 1',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Cotton Mist',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Oxford Grey',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Cashmere Dusk',
                      sub_variant_options: {},
                    },
                  ],
                },
                {
                  page: 'Level 2',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Dusty Grey',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Smoke',
                      sub_variant_options: {},
                    },
                    { enabled: true, variant: 'Ash', sub_variant_options: {} },
                    {
                      enabled: true,
                      variant: 'Espresso',
                      sub_variant_options: {},
                    },
                  ],
                },
                {
                  page: 'Level 3',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Glacier White',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Storm',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Wrought Iron',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Ebony',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Stone Grey',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Mushroom',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Blue Infusion',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'River Green',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Glacial White',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Countertop',
              variant_set_options: [
                {
                  page: 'Standard',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Bright White',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Flurry',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Elegant White',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Polaire',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Carbon',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Oyster Shell',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Thunder Grey',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Black Pearl',
                      sub_variant_options: {},
                    },
                  ],
                },
                {
                  page: 'Level 1',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Ivory Coast',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Venatino',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Luxe Noir',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Mountain Mist',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Imperial Grey',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Crystal White',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Stratum White',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Opulent Gold',
                      sub_variant_options: {},
                    },
                  ],
                },
                {
                  page: 'Level 2',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Verona',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Lincoln',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Smokey Almond',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Ivory Mist',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Titanium',
                      sub_variant_options: {},
                    },
                  ],
                },
                {
                  page: 'Level 3',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Perla Venata',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Super White',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Flooring',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Standard LVP',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Upgrade to Tile',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Tile and Backsplash',
              variant_set_options: [
                {
                  page: 'Level 1',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Passage Cleopatra',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Passage Bombshell',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Divine Marble',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Divine Graphite',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Divine Grigio',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Divine Cream',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Divine Light Grey',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Capri White',
                      sub_variant_options: {},
                    },
                  ],
                },
                {
                  page: 'Level 2',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Travertine Grey',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Travertine Beige',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Adrock Latte',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Adrock Espresso',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Westside Almond',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Westside Grey',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Westside Silver',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Westside White',
                      sub_variant_options: {},
                    },
                  ],
                },
                {
                  page: 'Level 3',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Unicolour Super White',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Unicolour Super Black',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Unicolour Light Grey',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Unicolour Charcoal',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Mayfair Stella Argento',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Mayfair Volkas Grigio',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Gatsby Pearl',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Pedestal Sink',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Standard',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Upgrade',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Ensuite',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Level One',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Level Two',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Level Three',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          lvs: 'General',
          lvs_options: [
            {
              variant_set: 'Wall Paint',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Summit CA019',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Crystal CA020',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Smoky CA032',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Fossil Grey CJ232',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Designer Grey CJ 231',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Platinum CA187',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Natural White EX290',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'LVP',
              variant_set_options: [
                {
                  page: 'Standard',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Chasing Ice',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Endless Summer',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Into the Wild',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Miss Sunshine',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Labyrinth',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Willow',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Whispered Herringbone',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Obscure Odyssey',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Nomad Herringbone',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Mystery Mile Herringbone',
                      sub_variant_options: {},
                    },
                  ],
                },
                {
                  page: 'Level 1',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Cool Hand Luke',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'The Natural',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Viva Zapata!',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'One-Eyed Jacks',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Marina Mist Chevron',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Wanderer',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Yacht Club Chevron',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Shipshape Chevron',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'The Hustler',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'High Noon Chevron',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Carpet',
              variant_set_options: [
                {
                  page: 'Standard',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Head Start Beach Lava',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Head Start Ocean Mist',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Head Start Gravel Stone',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Head Start August Grove',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Head Start Peaceful',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Head Start Gingerbread',
                      sub_variant_options: {},
                    },
                  ],
                },
                {
                  page: 'Level 1',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Smooth Talk Anew Grey',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Smooth Talk Bare Mineral',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Smooth Talk Urban Taupe',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Smooth Talk Halo',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Smooth Talk Tassel',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Smooth Talk Silver Mist',
                      sub_variant_options: {},
                    },
                  ],
                },
                {
                  page: 'Level 2',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Sea Point Snow Canyon',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Sea Point Even Tide',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Sea Point Stonehaven',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Sea Point Desert Flow',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Sea Point Castleberry',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Sea Point Almond Bark',
                      sub_variant_options: {},
                    },
                  ],
                },
                {
                  page: 'LVP',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Upgrade Carpet to LVP',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Door Style',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Two Panel',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Single Panel',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Two Panel Square Top',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Door Handle',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Winston',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Katella',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Halifax',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Prague',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Door Handle Finish',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Brushed Chrome',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Satin Nickle',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Matte Black',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Satin Nickel',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Railing',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Standard',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Level One',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Level Two',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Level Three',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Railing Profile',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Blank Iron Series',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: '7+2 Series',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'H-Cut',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Shaker',
                      sub_variant_options: {},
                    },
                    { enabled: true, variant: 'Wood', sub_variant_options: {} },
                    {
                      enabled: true,
                      variant: 'Glass',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Railing Handrail',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Standard',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Rectangle',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Rectangular',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Light Fixtures',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Standard',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Chrome',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Black Farmhouse',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Black and Gold',
                      sub_variant_options: {},
                    },
                    { enabled: true, variant: 'Gold', sub_variant_options: {} },
                    {
                      enabled: true,
                      variant: 'White and Gold',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Chrome Package',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Pantry Door',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Two Panel Square Top',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Single Panel',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Frosted Glass',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          lvs: 'Kitchen',
          lvs_options: [
            {
              variant_set: 'Layout',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Standard',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Level One',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Level Two',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Level Three',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Refrigerator',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Level 1',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Level 2',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Level 3',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Smart Appliance Refrigerator',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Black',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Cooktop',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Level 1',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Level 2',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Level 3',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Black',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Range Oven',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Standard',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Level 1',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Level 2',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Level 3',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Level 4',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Black Electric',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Black Gas',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Dishwasher',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Standard',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Level 1',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Level 2',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Black',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Wall Oven',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Level 1',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Level 2',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Black',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Microwave',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Standard',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Black',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Hood Fan',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Standard',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Black',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Flat Cabinet Hood',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Flat Tapered Cabinet Hood',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Tapered Hood',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Tapered Cabinet Hood',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Cabinet Profile',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    { enabled: true, variant: 'Slab', sub_variant_options: {} },
                    {
                      enabled: true,
                      variant: 'Shaker',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Micro Shaker',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Upper Cabinets',
              variant_set_options: [
                {
                  page: 'Standard',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Fashionista',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Free Spirit',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'First Class',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'The Chameleon',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Natural Affinity',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Black',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Suede Stone',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Fresh Linen',
                      sub_variant_options: {},
                    },
                  ],
                },
                {
                  page: 'Level 1',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Cotton Mist',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Oxford Grey',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Cashmere Dusk',
                      sub_variant_options: {},
                    },
                  ],
                },
                {
                  page: 'Level 2',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Dusty Grey',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Smoke',
                      sub_variant_options: {},
                    },
                    { enabled: true, variant: 'Ash', sub_variant_options: {} },
                    {
                      enabled: true,
                      variant: 'Espresso',
                      sub_variant_options: {},
                    },
                  ],
                },
                {
                  page: 'Level 3',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Glacier White',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Storm',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Wrought Iron',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Ebony',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Stone Grey',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Mushroom',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Blue Infusion',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'River Green',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Lower Cabinets',
              variant_set_options: [
                {
                  page: 'Standard',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Fashionista',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Free Spirit',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'First Class',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'The Chameleon',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Natural Affinity',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Black',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Suede Stone',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Fresh Linen',
                      sub_variant_options: {},
                    },
                  ],
                },
                {
                  page: 'Level 1',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Cotton Mist',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Oxford Grey',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Cashmere Dusk',
                      sub_variant_options: {},
                    },
                  ],
                },
                {
                  page: 'Level 2',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Dusty Grey',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Smoke',
                      sub_variant_options: {},
                    },
                    { enabled: true, variant: 'Ash', sub_variant_options: {} },
                    {
                      enabled: true,
                      variant: 'Espresso',
                      sub_variant_options: {},
                    },
                  ],
                },
                {
                  page: 'Level 3',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Glacier White',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Storm',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Wrought Iron',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Ebony',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Stone Grey',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Mushroom',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Blue Infusion',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'River Green',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Countertop',
              variant_set_options: [
                {
                  page: 'Standard',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Bright White',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Flurry',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Elegant White',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Polaire',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Carbon',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Oyster Shell',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Thunder Grey',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Black Pearl',
                      sub_variant_options: {},
                    },
                  ],
                },
                {
                  page: 'Level 1',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Ivory Coast',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Venatino',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Luxe Noir',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Mountain Mist',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Imperial Grey',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Crystal White',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Stratum White',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Opulent Gold',
                      sub_variant_options: {},
                    },
                  ],
                },
                {
                  page: 'Level 2',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Verona',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Lincoln',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Smokey Almond',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Ivory Mist',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Titanium',
                      sub_variant_options: {},
                    },
                  ],
                },
                {
                  page: 'Level 3',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Perla Venata',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Super White',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Backsplash',
              variant_set_options: [
                {
                  page: 'Standard',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Marlow Ames Cloud',
                      sub_variant_options: [
                        { enabled: true, sub_variant: 'Horizontal 50/50' },
                        { enabled: true, sub_variant: 'Vertical 50/50' },
                        { enabled: true, sub_variant: 'Horizontal Straight' },
                        { enabled: true, sub_variant: 'Vertical Straight' },
                      ],
                    },
                    {
                      enabled: true,
                      variant: 'Marlow Ames Mist',
                      sub_variant_options: [
                        { enabled: true, sub_variant: 'Horizontal 50/50' },
                        { enabled: true, sub_variant: 'Vertical 50/50' },
                        { enabled: true, sub_variant: 'Horizontal Straight' },
                        { enabled: true, sub_variant: 'Vertical Straight' },
                      ],
                    },
                    {
                      enabled: true,
                      variant: 'Villa Taupe',
                      sub_variant_options: [
                        { enabled: true, sub_variant: 'Horizontal 50/50' },
                        { enabled: true, sub_variant: 'Vertical 50/50' },
                        { enabled: true, sub_variant: 'Horizontal Straight' },
                        { enabled: true, sub_variant: 'Vertical Straight' },
                      ],
                    },
                  ],
                },
                {
                  page: 'Level 1',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'City Olympia Anthracite',
                      sub_variant_options: [
                        { enabled: true, sub_variant: 'Horizontal 50/50' },
                        { enabled: true, sub_variant: 'Vertical 50/50' },
                        { enabled: true, sub_variant: 'Horizontal Straight' },
                        { enabled: true, sub_variant: 'Vertical Straight' },
                      ],
                    },
                    {
                      enabled: true,
                      variant: 'City Olympia Bone',
                      sub_variant_options: [
                        { enabled: true, sub_variant: 'Horizontal 50/50' },
                        { enabled: true, sub_variant: 'Vertical 50/50' },
                        { enabled: true, sub_variant: 'Horizontal Straight' },
                        { enabled: true, sub_variant: 'Vertical Straight' },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Faucet',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Standard',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Upgrade',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Faucet Finish',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Chrome',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Matte Black',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Stainless Steel',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Sink',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Standard',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Single Undermount',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Dual Undermount',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Sink Finish',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Coal Black',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Anthracite',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Cinder',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Volcano Grey',
                      sub_variant_options: {},
                    },
                    { enabled: true, variant: 'Cafe', sub_variant_options: {} },
                    {
                      enabled: true,
                      variant: 'White',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Pot Filler',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'No Pot Filler',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Add Pot Filler',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
            {
              variant_set: 'Spice Kitchen Hood',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Standard',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Level 1 Undercabinet Hood Fan',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Level 1 Hood Fan',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Black',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          lvs: 'Laundry',
          lvs_options: [
            {
              variant_set: 'Laundry',
              variant_set_options: [
                {
                  page: '',
                  page_options: [
                    {
                      enabled: true,
                      variant: 'Standard',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Upgrade',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Black',
                      sub_variant_options: {},
                    },
                    {
                      enabled: true,
                      variant: 'Level 1',
                      sub_variant_options: {},
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
}

// Build the flattened options list
function buildCommunityOptions(): CommunityOption[] {
  const allOptions: CommunityOption[] = []

  // Exterior options
  allOptions.push(...flattenCategoryOptions('exterior', rawData.Exterior))

  // Interior options
  allOptions.push(...flattenCategoryOptions('interior', rawData.Interior))

  // Model-specific options
  for (const model of rawData.Models) {
    allOptions.push(
      ...flattenCategoryOptions(
        'model-elevation',
        model.Elevations,
        model.Name,
      ),
    )
    allOptions.push(
      ...flattenCategoryOptions(
        'model-structural',
        model.Structural,
        model.Name,
      ),
    )
  }

  return allOptions
}

export const initialCommunityOptions: CommunityOption[] =
  buildCommunityOptions()

// Helper to get unique LVS groups for a section
export function getLvsGroups(
  options: CommunityOption[],
  section?: string,
  modelName?: string,
): string[] {
  let filtered = options
  if (section) {
    filtered = filtered.filter((o) => o.section === section)
  }
  if (modelName) {
    filtered = filtered.filter((o) => o.modelName === modelName)
  }
  return [...new Set(filtered.map((o) => o.lvs))]
}

// Helper to get unique variant sets within an LVS
export function getVariantSets(
  options: CommunityOption[],
  lvs: string,
  section?: string,
): string[] {
  let filtered = options.filter((o) => o.lvs === lvs)
  if (section) {
    filtered = filtered.filter((o) => o.section === section)
  }
  return [...new Set(filtered.map((o) => o.variantSet))]
}

// Model info for display
export const communityModels = rawData.Models.map((m) => ({
  name: m.Name,
  beds: m.Beds,
  baths: m.Baths,
  price: m.Price,
  surface: m.Surface,
  location: m.Location,
  enabled: m.Enabled,
  info: m.Additional_Info,
}))
